import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Truck } from "lucide-react";
import { Card, Btn } from "@/components/admin/ui";
import { API } from "@/config";

export const Drivers = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/employees`);
      setDrivers(data.employees || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load drivers");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email are required."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/employees/invite`, form);
      setLastInvite({ invite_url: data.invite_url, email_sent: data.email_sent, email: form.email });
      toast.success(data.email_sent ? "Invite emailed 🎉" : "Invite created — copy the link below");
      setForm({ name: "", email: "", phone: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create invite");
    } finally { setBusy(false); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove driver ${name}? They'll lose access immediately.`)) return;
    try { await axios.delete(`${API}/admin/employees/${id}`); toast.success("Driver removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const copyInvite = async () => {
    if (!lastInvite?.invite_url) return;
    try { await navigator.clipboard.writeText(lastInvite.invite_url); toast.success("Copied to clipboard"); }
    catch { toast.error("Copy failed — long-press the link instead."); }
  };

  return (
    <div className="space-y-6" data-testid="drivers-page">
      <div className="flex items-center gap-3">
        <Truck className="w-6 h-6 text-[#FFD166] flex-shrink-0" />
        <h1 className="font-serif text-3xl">Drivers</h1>
      </div>
      <p className="text-sm text-[#B9C8D5] max-w-2xl">Invite a driver by email — they'll get a one-time link to pick their own password. Invites expire in 48 hours.</p>

      <Card>
        <div className="p-5">
          <h2 className="font-serif text-xl mb-4">Invite a new driver</h2>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input required className="input" placeholder="Driver name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="driver-name" />
            <input required type="email" className="input" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="driver-email" />
            <input className="input" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="driver-phone" />
            <div className="md:col-span-3 flex justify-end">
              <Btn onClick={submit} disabled={busy} data-testid="driver-invite-submit"><Plus className="w-4 h-4" /> {busy ? "Sending…" : "Send invite"}</Btn>
            </div>
          </form>

          {lastInvite && (
            <div className="mt-5 p-4 rounded-lg border border-[#FFD166]/30 bg-[#FFD166]/5" data-testid="last-invite">
              <div className="text-xs text-[#B9C8D5]">
                {lastInvite.email_sent
                  ? <>Invite emailed to <b className="text-[#FFD166]">{lastInvite.email}</b>. You can also share this direct link:</>
                  : <>Email delivery failed — share this direct link with <b className="text-[#FFD166]">{lastInvite.email}</b>:</>}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 text-xs bg-black/40 rounded px-3 py-2 text-[#FFD166] break-all">{lastInvite.invite_url}</code>
                <Btn onClick={copyInvite} data-testid="copy-invite-btn"><Copy className="w-4 h-4" /> Copy</Btn>
              </div>
            </div>
          )}
        </div>
      </Card>

      <Card>
        <div className="p-5">
          <h2 className="font-serif text-xl mb-4">Active drivers</h2>
          {loading ? (
            <p className="text-[#B9C8D5]">Loading…</p>
          ) : drivers.length === 0 ? (
            <p className="text-[#B9C8D5]">No drivers yet — invite your first one above.</p>
          ) : (
            <>
              {/* Mobile card list */}
              <div className="md:hidden space-y-3" data-testid="driver-list-mobile">
                {drivers.map((d) => (
                  <div key={d.id} className="border border-[#FFD166]/15 rounded-xl p-3 bg-black/20">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-[#F6F1E4] truncate">{d.name || "—"}</div>
                        <div className="text-xs text-[#B9C8D5] truncate">{d.email}</div>
                        {d.phone && <div className="text-xs text-[#B9C8D5] mt-0.5">☎ {d.phone}</div>}
                        <div className="text-xs mt-1">
                          {d.activated_at
                            ? <span className="text-[#7EE787]">✓ Active</span>
                            : <span className="text-[#FFD166]">⏳ Pending activation</span>}
                          {d.invited_at && <span className="text-[#B9C8D5]"> · Invited {new Date(d.invited_at).toLocaleDateString()}</span>}
                        </div>
                      </div>
                      <Btn variant="danger" onClick={() => remove(d.id, d.name || d.email)} data-testid={`driver-remove-${d.id}`} className="flex-shrink-0">
                        <Trash2 className="w-4 h-4" />
                      </Btn>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                <thead className="text-left text-[#B9C8D5]">
                  <tr>
                    <th className="py-2 pr-4">Name</th>
                    <th className="py-2 pr-4">Email</th>
                    <th className="py-2 pr-4">Phone</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Invited</th>
                    <th className="py-2 pr-4"></th>
                  </tr>
                </thead>
                <tbody data-testid="driver-list">
                  {drivers.map((d) => (
                    <tr key={d.id} className="border-t border-[#FFD166]/10">
                      <td className="py-2 pr-4">{d.name || "—"}</td>
                      <td className="py-2 pr-4">{d.email}</td>
                      <td className="py-2 pr-4">{d.phone || "—"}</td>
                      <td className="py-2 pr-4">
                        {d.activated_at
                          ? <span className="text-[#7EE787]">✓ Active</span>
                          : <span className="text-[#FFD166]">⏳ Pending activation</span>}
                      </td>
                      <td className="py-2 pr-4 text-[#B9C8D5]">{d.invited_at ? new Date(d.invited_at).toLocaleDateString() : "—"}</td>
                      <td className="py-2 pr-4 text-right">
                        <Btn variant="danger" onClick={() => remove(d.id, d.name || d.email)} data-testid={`driver-remove-${d.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Btn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
};
